const recsData = {};
const rendered = new Set();
const viewed = new Set();
const ids = new Map();

const uuidv4 = () => {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (
      c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
    ).toString(16)
  );
};

const pageLoadId = uuidv4();

const getCookie = (name) => {
  const cookie = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${name}=`));
  if (cookie) return decodeURIComponent(cookie.split("=")[1]);
  return null;
};

const setCookie = (name, value, days = null) => {
  document.cookie = `${name}=${encodeURIComponent(value)};${
    days ? `max-age=${days * 24 * 60 * 60}` : ""
  };path=/`;
  return value;
};

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("qatcopen", () => {
      this.classList.add("qatc-open");
    });
    this.addEventListener("qatcclose", () => {
      this.classList.remove("qatc-open");
    });
    this.addEventListener("qatcheightchange", (e) => {
      this.style.setProperty("--qatc-height", `${e.detail.height}px`);
    });

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.init();
          observer.disconnect();
        }
      },
      { rootMargin: "-25% 0px 0px 0px" }
    );

    observer.observe(this);
  }

  async init() {
    const params = new URLSearchParams();

    const product = this.getAttribute("product");
    if (product && product.length > 0) params.set("product", product);

    const userCookie = getCookie("ssUserId");
    this.userId = userCookie
      ? userCookie
      : setCookie("ssUserId", uuidv4(), 365);

    const sessionCookie = getCookie("ssSessionIdNamespace");
    this.sessionId = sessionCookie
      ? sessionCookie
      : setCookie("ssSessionIdNamespace", uuidv4());

    this.placement = this.getAttribute("placement");
    this.tag = this.getAttribute("tags");

    const profileId = ids.has(this.tag) ? ids.get(this.tag) : uuidv4();
    ids.set(this.tag, profileId);

    const profileEventData = {
      profile: {
        tag: this.tag,
        placement: this.placement,
        seed: this.getAttribute("seed")
          ? this.getAttribute("seed").split(",")
          : [],
      },
    };

    const ssId = theme.searchspringId;

    params.set("tags", this.getAttribute("tags"));
    params.set("limits", parseInt(this.getAttribute("limit") || "20"));
    params.set("siteId", ssId);

    const hiddenProducts = JSON.parse(this.getAttribute("hidden-products"));

    const viewedCookie = getCookie("ssViewedProducts");
    if (viewedCookie) {
      const viewed = viewedCookie.split(",");
      viewed.forEach((item) => params.append("lastViewed", item));
    }

    const url = `https://${ssId}.a.searchspring.io/boost/${ssId}/recommend?${params.toString()}`;

    if (!recsData[url]) {
      const resp = await fetch(url);
      recsData[url] = await resp.json();
    }

    const data = recsData[url];

    const results = data[0].results;
    const template = document.getElementById("productCardTemplate");

    const carousel = this.querySelector("card-carousel");
    const items = carousel.querySelector("card-carousel-items");
    items.innerHTML = "";

    const productRenderEvents = [];
    productRenderEvents.push({
      id: profileId,
      type: "profile.render",
      eventData: profileEventData,
    });

    let productImpressionEvents = [];

    if (results.length == 0) {
      this.closest(".product-recommendations").remove();
      return;
    }

    for (const result of results) {
      if (hiddenProducts && hiddenProducts.includes(parseInt(result.id)))
        continue;

      const card = template.content.cloneNode(true);

      const image = card.querySelector(".card__image");
      image.src = result.mappings.core.imageUrl;
      image.width = 400;
      image.height = 500;
      image.style.backgroundImage = `url(${result.mappings.core.imageUrl.replace(
        "500x",
        "50x"
      )})`;
      image.style.backgroundSize = "cover";

      const link = card.querySelector(".card__heading a");
      link.innerHTML = result.mappings.core.name;
      link.href = result.mappings.core.url;

      const productEventId = ids.has(result.id) ? ids.get(result.id) : uuidv4();
      ids.set(result.id, productEventId);

      const productEventData = {
        product: {
          id: result.id,
          mappings: result.mappings,
        },
      };

      if (!rendered.has(result.id)) {
        productRenderEvents.push({
          id: productEventId,
          type: "profile.product.render",
          eventData: productEventData,
          parentId: profileId,
        });

        rendered.add(result.id);
      }

      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          if (!viewed.has(result.id)) {
            if (productImpressionEvents.length == 0) {
              setTimeout(() => {
                const events = [];
                if (!viewed.has(profileId)) {
                  viewed.add(profileId);
                  events.push({
                    id: profileId,
                    type: "profile.impression",
                    eventData: profileEventData,
                  });
                }
                events.push(...productImpressionEvents);

                this.fireEvents(events);
                productImpressionEvents = [];
              }, 1000);
            }

            productImpressionEvents.push({
              id: productEventId,
              type: "profile.product.impression",
              eventData: productEventData,
              parentId: profileId,
            });
            viewed.add(result.id);
          }

          observer.disconnect();
        }
      });

      observer.observe(card.querySelector(".card"));

      link.addEventListener("mousedown", (e) => {
        const events = [
          { id: profileId, type: "profile.click", eventData: profileEventData },
          {
            id: productEventId,
            type: "profile.product.click",
            eventData: productEventData,
            parentId: profileId,
          },
        ];
        this.fireEvents(events);
      });

      const wishlistButton = card.querySelector("wishlist-add-button");
      wishlistButton.dataset.handle = result.attributes.handle;
      wishlistButton.dataset.id = result.id;
      wishlistButton.dataset.title = result.mappings.core.name;
      wishlistButton.dataset.isBundle =
        result.attributes.ss_tags.includes("BundleProduct");

      //Begin Tontine Price logic
      let tontinePrice = false;
      let tontineMin;
      let tontineMax;
      if (
        window.TontineApp &&
        window.TontineApp.variantPrices &&
        window.TontineApp.variantPrices[result.attributes.variant_id]
      ) {
        const tontineVariant =
          window.TontineApp.variantPrices[result.attributes.variant_id];
        const tontineBucket = window.TontineApp.app.variation.get(
          tontineVariant.trafficSplit,
          tontineVariant.multiarmed
        );
        if (tontineBucket == "b") {
          tontinePrice = true;
          const tontineProductVariants = Object.values(
            window.TontineApp.variantPrices
          )
            .filter((v) => v.product == tontineVariant.product)
            .map((v) => v.number);
          tontineMin = Math.min(...tontineProductVariants);
          tontineMax = Math.max(...tontineProductVariants);
        }
      }
      // End Tontine Price

      const price = card.querySelector(".card__price");
      const minPrice = result.attributes.ss_price_range
        ? result.attributes.ss_price_range[0]
        : result.mappings.core.price;
      const maxPrice = result.attributes.ss_price_range
        ? result.attributes.ss_price_range[1]
        : result.mappings.core.price;
      const minComparePrice = result.attributes.ss_msrp_range
        ? result.attributes.ss_msrp_range[0]
        : result.mappings.core.msrp;
      const maxComparePrice = result.attributes.ss_msrp_range
        ? result.attributes.ss_msrp_range[1]
        : result.mappings.core.msrp;

      price.innerHTML = `
				<dynamic-price lazy 
					data-handle="${result.attributes.handle}" 
					data-product-id="${result.id}"
					data-min-price="${!isNaN(tontineMin) ? tontineMin / 100 : minPrice}"
					data-max-price="${!isNaN(tontineMax) ? tontineMax / 100 : maxPrice}"
					data-min-compare-price="${minComparePrice}"
					data-max-compare-price="${maxComparePrice}"
					data-region="${theme.countryCode}"
				></dynamic-price>`;

      const rating = card.querySelector(".review-rating");
      if (
        !result.mappings.core.ratingCount ||
        result.mappings.core.ratingCount == 0
      )
        rating.remove();
      else {
        rating.style.setProperty("--stars", result.mappings.core.rating);
        const starText = rating.querySelector(".review-rating__stars");
        starText.parentNode.style.visibility = "";
        starText.innerHTML = result.mappings.core.rating;
        rating.querySelector(
          ".review-rating__count"
        ).innerHTML = `(${result.mappings.core.ratingCount})`;
      }

      if (
        this.getAttribute("quick-add") == "true" &&
        !result.attributes.handle.includes("build-your-own")
      ) {
        const qatc = document.createElement("quick-add-to-cart");
        qatc.setAttribute("is-button", "");
        qatc.innerHTML = "Add to Cart";
        qatc.dataset.handle = result.attributes.handle;
        card.querySelector(".card").appendChild(qatc);

        const qatcDesktop = document.createElement("quick-add-to-cart");
        qatcDesktop.dataset.handle = result.attributes.handle;
        qatcDesktop.setAttribute("dropdown", "true");
        qatcDesktop.setAttribute("init", "true");
        qatcDesktop.setAttribute("data-source", "product-recommendations");
        qatcDesktop.innerHTML = "Add to Cart";
        card.querySelector(".card").appendChild(qatcDesktop);
      }

      const rec = document.createElement("li");
      rec.addEventListener("qatcadd", (e) => {
        setTimeout(() => {
          rec.classList.add("transition-out");
          setTimeout(() => {
            rec.remove();
          }, 350);
        }, 1500);
      });
      rec.className = "recommendation";
      rec.appendChild(card);
      items.appendChild(rec);
    }

    if (carousel.update) carousel.update();
    this.dispatchEvent(new CustomEvent("loaded"));

    if (productRenderEvents.length > 1) this.fireEvents(productRenderEvents);
  }

  getEventPayload(id, type, eventData = {}, parentId = null) {
    return {
      id: id,
      type: type,
      category: "searchspring.recommendations.user-interactions",
      pid: parentId,
      event: {
        ...eventData,
        context: {
          type: "product-recommendation",
          tag: this.tag,
          placement: this.placement,
        },
      },
      context: {
        pageLoadId,
        userId: this.userId,
        sessionId: this.sessionId,
        website: {
          trackingCode: theme.searchspringId,
        },
      },
    };
  }

  async fireEvents(events) {
    const endpoint = `https://beacon.searchspring.io/beacon`;
    const payload = events.map((e) =>
      this.getEventPayload(e.id, e.type, e.eventData, e.parentId)
    );

    await fetch(endpoint, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    });
  }
}

customElements.define("product-recommendations", ProductRecommendations);
